import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CONTACT } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "Custom Design",
  "Engagement Ring",
  "Repair",
  "Cleaning",
  "Ring Sizing",
  "Stone Replacement",
  "Appraisal",
  "Watch Battery",
  "Engraving",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/inquiries`, form);
      setStatus({
        state: "success",
        msg: "Thank you — a member of the atelier will write back within one working day.",
      });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setStatus({ state: "error", msg: String(msg) });
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative bg-[#F7F5F2] px-6 md:px-10 py-28 md:py-40 border-t border-[#1C1C1A]/10"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Left column */}
        <div className="lg:col-span-5 space-y-12">
          <div>
            <p className="text-[10px] uppercase tracking-[0.32em] font-sans text-[#1C1C1A]/70 mb-5">
              Ch. 08 — Visit the Atelier
            </p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-[-0.02em]"
            >
              Tell us
              <br />
              <span className="italic text-[#9A8B73]">your dream.</span>
            </motion.h2>
            <p className="mt-6 font-sans text-base leading-relaxed max-w-md text-[#1C1C1A]/80">
              Whether it&apos;s a proposal, a milestone, or an heirloom to
              revive — write us a few lines and we&apos;ll take it from there.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-sans text-[#1C1C1A]/60 mb-2">
                Address
              </p>
              <p className="font-serif text-lg leading-snug">
                47, 2020 Sherwood Dr.
                <br />
                Sherwood Park Mall
                <br />
                Sherwood Park, AB T8A 3H9
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-sans text-[#1C1C1A]/60 mb-2">
                Hours
              </p>
              <p className="font-serif text-lg leading-snug">
                Mon — Fri · 10:00 — 20:00
                <br />
                Sat · 10:00 — 19:00
                <br />
                Sun · 11:00 — 18:00
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-sans text-[#1C1C1A]/60 mb-2">
                Telephone
              </p>
              <a
                href="tel:+15879841800"
                className="link-underline font-serif text-lg"
              >
                +1 (587) 984-1800
              </a>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-sans text-[#1C1C1A]/60 mb-2">
                Email
              </p>
              <a
                href="mailto:diamantjeweller@gmail.com"
                className="link-underline font-serif text-lg break-words"
              >
                diamantjeweller@gmail.com
              </a>
            </div>
          </div>
        </div>

        {/* Form column */}
        <div className="lg:col-span-7">
          <form
            data-testid={CONTACT.form}
            onSubmit={submit}
            className="space-y-8"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] font-sans text-[#1C1C1A]/60">
                  Your Name
                </span>
                <input
                  data-testid={CONTACT.name}
                  type="text"
                  required
                  value={form.name}
                  onChange={set("name")}
                  className="editorial-input mt-2"
                  placeholder="Name"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] font-sans text-[#1C1C1A]/60">
                  Email
                </span>
                <input
                  data-testid={CONTACT.email}
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  className="editorial-input mt-2"
                  placeholder="you@example.com"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] font-sans text-[#1C1C1A]/60">
                  Phone (optional)
                </span>
                <input
                  data-testid={CONTACT.phone}
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  className="editorial-input mt-2"
                  placeholder="+1 —"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] font-sans text-[#1C1C1A]/60">
                  Service
                </span>
                <select
                  data-testid={CONTACT.service}
                  value={form.service}
                  onChange={set("service")}
                  className="editorial-input mt-2 appearance-none bg-transparent cursor-pointer"
                >
                  <option value="">Select a service…</option>
                  {serviceOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.3em] font-sans text-[#1C1C1A]/60">
                Your Dream
              </span>
              <textarea
                data-testid={CONTACT.message}
                required
                value={form.message}
                onChange={set("message")}
                rows={4}
                className="editorial-input mt-2 resize-none"
                placeholder="A short description — an occasion, a stone, an inspiration…"
              />
            </label>

            <div className="flex items-center justify-between pt-6">
              <p className="text-[10px] uppercase tracking-[0.3em] font-sans text-[#1C1C1A]/50 max-w-[240px]">
                We reply within one working day. Your details stay in the
                atelier.
              </p>

              <button
                type="submit"
                data-testid={CONTACT.submit}
                disabled={status.state === "loading"}
                className="group inline-flex items-center gap-3 text-xs uppercase tracking-[0.24em] font-sans border border-[#1C1C1A] px-6 py-4 hover:bg-[#1C1C1A] hover:text-[#F7F5F2] transition-colors duration-500 disabled:opacity-60"
              >
                {status.state === "loading" ? "Sending…" : "Send Inquiry"}
                <span className="transition-transform duration-500 group-hover:translate-x-1">
                  →
                </span>
              </button>
            </div>

            {status.state === "success" && (
              <p
                data-testid={CONTACT.success}
                className="text-sm font-sans text-[#1C1C1A] border-t border-[#1C1C1A]/20 pt-6"
              >
                {status.msg}
              </p>
            )}
            {status.state === "error" && (
              <p
                data-testid={CONTACT.error}
                className="text-sm font-sans text-red-700 border-t border-red-700/30 pt-6"
              >
                {status.msg}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
